import { useState, useMemo, useEffect, useRef } from "react";
import { Sparkles, X, ArrowRight } from "lucide-react";
import ProductCard from "./ProductCard";

const HOME_LIMIT = 12;

const PRICE_BUCKETS = [
  { id: "b1", label: "Under $50",   min: 0,      max: 49.99    },
  { id: "b2", label: "$50 – $89",   min: 50,     max: 89.99    },
  { id: "b3", label: "$90 – $200",  min: 90,     max: 200      },
  { id: "b4", label: "$200 – $250", min: 200.01, max: 250      },
  { id: "b5", label: "$250+",       min: 250.01, max: Infinity  },
];

export default function ExploreBouquets({ bouquets, onNavigate, loading }) {
  const [occasion, setOccasion] = useState("all");
  const [bucket, setBucket]     = useState("all");

  // Sticky-bar shadow only while it's stuck.
  const [stuck, setStuck] = useState(false);
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting),
      { rootMargin: "-1px 0px 0px 0px", threshold: [0, 1] },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const occasions = useMemo(() => {
    const seen = new Map();
    for (const b of bouquets) {
      for (const o of b.occasions ?? []) {
        if (!o) continue;
        const key = o.toLowerCase();
        if (!seen.has(key)) seen.set(key, o);
      }
    }
    return Array.from(seen.values()).sort();
  }, [bouquets]);

  const buckets = PRICE_BUCKETS;

  const filtered = useMemo(() => {
    return bouquets.filter((b) => {
      if (occasion !== "all") {
        const matches = (b.occasions ?? []).some(
          (o) => o.toLowerCase() === occasion,
        );
        if (!matches) return false;
      }
      if (bucket !== "all") {
        const bk = buckets.find((x) => x.id === bucket);
        if (!bk) return false;
        const price = Number(b.price);
        if (price < bk.min || price > bk.max) return false;
      }
      return true;
    });
  }, [bouquets, occasion, bucket, buckets]);

  const visible = filtered.slice(0, HOME_LIMIT);
  const hasFilters = occasion !== "all" || bucket !== "all";

  // Helper for the empty-state widening hint.
  const widerBucket = useMemo(() => {
    if (bucket === "all") return null;
    const idx = buckets.findIndex((b) => b.id === bucket);
    if (idx === -1) return null;
    // Prefer one bucket up; if at top, try one down.
    return buckets[idx + 1] ?? buckets[idx - 1] ?? null;
  }, [bucket, buckets]);

  return (
    <section className="py-12 md:py-20 bg-brand-cream-100 relative">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center md:text-left mb-6 md:mb-8">
          <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-brand-crimson-400" />
            <p className="font-body text-[10px] text-brand-crimson-400 tracking-[0.3em] uppercase font-semibold">
              Explore the Collection
            </p>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl text-brand-forest-700 leading-tight">
            Find Your Perfect Bouquet
          </h2>
          <p className="font-elegant italic text-brand-forest-400 text-sm md:text-base mt-1.5">
            Pick an occasion, set your budget — we'll do the rest.
          </p>
        </div>

        {/* Sentinel for the sticky shadow trigger */}
        <div ref={sentinelRef} aria-hidden="true" />

        {/* Sticky filter bar */}
        <div
          className={`sticky top-0 z-30 -mx-4 md:mx-0 px-4 md:px-0 pt-3 pb-2 md:pt-4 md:pb-3
            bg-brand-cream-100/97 backdrop-blur-md transition-all duration-200
            ${stuck ? "shadow-[0_4px_24px_-4px_rgba(0,0,0,0.10)]" : ""}`}
        >
          {/* Two filter rows separated by a faint divider */}
          <div className="space-y-2.5 md:space-y-3">

            {/* ── Occasion row ── */}
            <div className="flex items-center gap-3">
              {/* Label — desktop only, inline with chips */}
              <span className="hidden md:block font-body text-[10px] font-semibold text-brand-forest-400 uppercase tracking-[0.2em] shrink-0 w-16">
                Occasion
              </span>
              <div
                className="flex gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:hidden -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap pb-0.5"
                style={{ scrollbarWidth: "none" }}
              >
                <OccasionChip
                  active={occasion === "all"}
                  onClick={() => setOccasion("all")}
                  label="All"
                />
                {occasions.map((o) => (
                  <OccasionChip
                    key={o}
                    active={occasion === o.toLowerCase()}
                    onClick={() => setOccasion(o.toLowerCase())}
                    label={o}
                  />
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-brand-cream-200 -mx-4 md:mx-0" />

            {/* ── Price row ── */}
            <div className="flex items-center gap-3">
              <span className="hidden md:block font-body text-[10px] font-semibold text-brand-forest-400 uppercase tracking-[0.2em] shrink-0 w-16">
                Budget
              </span>
              <div
                className="flex gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:hidden -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap pb-0.5"
                style={{ scrollbarWidth: "none" }}
              >
                <PriceChip
                  active={bucket === "all"}
                  onClick={() => setBucket("all")}
                  label="Any price"
                />
                {buckets.map((b) => (
                  <PriceChip
                    key={b.id}
                    active={bucket === b.id}
                    onClick={() => setBucket(b.id)}
                    label={b.label}
                  />
                ))}
              </div>
            </div>

          </div>

          {/* Result count + clear */}
          {(hasFilters || filtered.length > 0) && (
            <div className="flex items-center justify-between gap-3 mt-2.5 pt-2.5 border-t border-brand-cream-200/70 md:ml-[4.75rem]">
              <p className="font-body text-xs text-brand-forest-400">
                {loading ? "Loading…" : (
                  <>
                    <span className="font-semibold text-brand-forest-700">{filtered.length}</span>
                    {" "}{filtered.length === 1 ? "bouquet" : "bouquets"}
                    {hasFilters && <span className="text-brand-forest-300"> found</span>}
                  </>
                )}
              </p>
              {hasFilters && (
                <button
                  onClick={() => { setOccasion("all"); setBucket("all"); }}
                  className="flex items-center gap-1 font-body text-xs text-brand-crimson-500 hover:text-brand-crimson-600 font-semibold transition-colors"
                >
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
            </div>
          )}
        </div>

        {/* Grid */}
        <div className="mt-6 md:mt-8">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl bg-brand-cream-50 border border-brand-cream-200 aspect-3/4 animate-pulse"
                />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <EmptyState
              occasion={occasion}
              bucket={bucket}
              buckets={buckets}
              widerBucket={widerBucket}
              onWiden={() => setBucket(widerBucket?.id ?? "all")}
              onClear={() => { setOccasion("all"); setBucket("all"); }}
            />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {visible.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}

          {/* View all → /shop */}
          {!loading && filtered.length > visible.length && (
            <div className="mt-6 md:mt-10 flex justify-center">
              <button
                onClick={() => onNavigate?.("shop")}
                className="flex items-center gap-2 bg-brand-forest-500 hover:bg-brand-forest-600 text-white font-body text-sm px-8 py-3.5 rounded-full shadow-md hover:shadow-lg transition-all"
              >
                View all bouquets
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {!loading && filtered.length > 0 && filtered.length <= visible.length && (
            <div className="mt-6 md:mt-10 flex justify-center">
              <button
                onClick={() => onNavigate?.("shop")}
                className="inline-flex items-center gap-1.5 font-body text-sm text-brand-forest-500 border border-brand-forest-300 hover:bg-brand-forest-500 hover:text-white hover:border-brand-forest-500 px-6 py-2.5 rounded-full transition-all"
              >
                Browse the full shop
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function OccasionChip({ active, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 whitespace-nowrap px-3.5 py-1.5 rounded-full font-body text-sm border transition-all duration-200 ${
        active
          ? "bg-brand-forest-600 text-white border-brand-forest-600 shadow-sm"
          : "bg-white text-brand-forest-600 border-brand-cream-300 hover:border-brand-forest-400 hover:bg-brand-cream-50"
      }`}
    >
      {label}
    </button>
  );
}

function PriceChip({ active, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 whitespace-nowrap px-3.5 py-1.5 rounded-full font-body text-sm border transition-all duration-200 ${
        active
          ? "bg-brand-crimson-500 text-white border-brand-crimson-500 shadow-sm"
          : "bg-white text-brand-forest-500 border-brand-cream-300 hover:border-brand-crimson-300 hover:bg-brand-cream-50"
      }`}
    >
      {label}
    </button>
  );
}

function EmptyState({ occasion, bucket, buckets, widerBucket, onWiden, onClear }) {
  const occasionLabel = occasion === "all" ? "" : occasion;
  const bucketLabel   = bucket === "all"
    ? ""
    : buckets.find((b) => b.id === bucket)?.label ?? "";

  let line;
  if (occasionLabel && bucketLabel) {
    line = `No ${occasionLabel} bouquets in ${bucketLabel}.`;
  } else if (occasionLabel) {
    line = `No bouquets found for ${occasionLabel}.`;
  } else if (bucketLabel) {
    line = `No bouquets in ${bucketLabel} right now.`;
  } else {
    line = "No bouquets match these filters.";
  }

  return (
    <div className="flex flex-col items-center text-center py-12 px-4 bg-brand-cream-50 border border-brand-cream-200 rounded-3xl">
      <p className="font-display text-lg text-brand-forest-700 mb-2">{line}</p>
      <p className="font-body text-sm text-brand-forest-400 mb-5 max-w-sm">
        Widen your search and we'll find you something beautiful.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {widerBucket && (
          <button
            onClick={onWiden}
            className="bg-brand-forest-500 hover:bg-brand-forest-600 text-white font-body text-sm px-5 py-2.5 rounded-full shadow-sm transition-all"
          >
            Try {widerBucket.label}
          </button>
        )}
        <button
          onClick={onClear}
          className="border border-brand-cream-300 text-brand-forest-600 hover:bg-white font-body text-sm px-5 py-2.5 rounded-full transition-all"
        >
          Clear filters
        </button>
      </div>
    </div>
  );
}
